/**
 * DashboardHeader - personalised greeting + weekly summary + header actions.
 * "Hallo {name} · {Wochentag, Datum}" / "Du hast diese Woche N Bilder generiert."
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Images, Sparkles } from 'lucide-react'
import { Button } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'

interface DashboardHeaderProps {
  username: string
  imagesThisWeek: number | null
}

export function DashboardHeader({ username, imagesThisWeek }: DashboardHeaderProps) {
  const { t, i18n } = useTranslation()
  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  return (
    <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1
          id={EID['dashboard-heading']}
          className="text-2xl lg:text-3xl font-bold tracking-tight text-neutral-900 dark:text-white"
        >
          {t('dashboardOverview.greeting', { name: username })}{' '}
          <span className="font-normal text-neutral-500">· {today}</span>
        </h1>
        {imagesThisWeek !== null && (
          <p
            data-testid="dashboard-week-summary"
            className="mt-1 text-sm text-neutral-500 dark:text-neutral-500"
          >
            {t('dashboardOverview.weekSummary', { count: imagesThisWeek })}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Link to="/v2/tools" id={EID['dashboard-header-gallery-link']}>
          <Button variant="secondary" size="sm">
            <Images className="mr-2 h-4 w-4" />
            {t('dashboardOverview.gallery')}
          </Button>
        </Link>
        <Link to="/v2/tools/studio/single-shot" id={EID['dashboard-header-generate-btn']}>
          <Button variant="primary" size="sm">
            <Sparkles className="mr-2 h-4 w-4" />
            {t('dashboardOverview.generate')}
          </Button>
        </Link>
      </div>
    </header>
  )
}
